import { useEffect, useState } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import AdaptiveImageCapture from '../../pages/AdaptiveAssessmentPage/components/AdaptiveImageCaptureProvider';
import ImageCapture from '../ImageCapture';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { setPhotoUploadPause } from '../../store/assessment/assessmentSlice';

const GenericCameraComponent = () => {
  const { is_adaptive, camera_permission, photo_upload_pause } = useAppSelector(
    (state) => state.assessment
  );

  const [key, setKey] = useState(0);
  const [cameraEnabled, setCameraEnabled] = useState(true);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const func = async () => {
      try {
        // Check if Permissions API is supported
        if (navigator.permissions && navigator.permissions.query) {
          try {
            // Try the standard way first
            // Type assertion to handle TypeScript issue with 'camera' not being in PermissionName
            const camera_perm = await navigator.permissions.query({
              name: 'camera' as PermissionName,
            });
            camera_perm.onchange = (evt) => {
              const allowed = camera_perm.state === 'granted';
              if (allowed) {
                setKey((prev) => prev + 1);
              } else {
                console.error('Camera permission revoked by user');
                setKey((prev) => prev + 1);
              }
            };
          } catch (err) {
            // Safari might need this fallback approach
            // Safari may throw an error for 'camera' as it's not in their recognized permission names
            console.error(
              'Standard permissions query failed, trying alternative methods'
            );

            // For Safari, we can test camera access directly as a fallback
            testCameraAccess();
          }
        } else {
          // Fallback for browsers without Permissions API
          console.error('Permissions API not supported, using fallback method');
          testCameraAccess();
        }
      } catch (err) {
        console.error('Error in camera permission handling:', err);
      }
    };

    // Function to test camera access directly
    const testCameraAccess = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        // Clean up the test stream
        stream.getTracks().forEach((track) => track.stop());
        setKey((prev) => prev + 1);
      } catch (err) {
        console.error('Camera access denied or not available');
        setKey((prev) => prev + 1);
      }
    };

    func();

    function enableCamera() {
      setCameraEnabled(true);
    }

    function disableCamera() {
      setCameraEnabled(false);
    }

    document.addEventListener('disableCamera', disableCamera);
    document.addEventListener('enableCamera', enableCamera);

    return () => {
      document.removeEventListener('disableCamera', disableCamera);
      document.removeEventListener('enableCamera', enableCamera);
    };
  }, []);

  useEffect(() => {
    if (!camera_permission && photo_upload_pause) {
      dispatch(setPhotoUploadPause(false));
    }
  }, [photo_upload_pause, camera_permission]);

  const { accessKey } = useAppSelector((state) => state.time);

  if (!camera_permission || !cameraEnabled) return <></>;

  if (is_adaptive)
    return <AdaptiveImageCapture key={key} accessKey={accessKey} />;

  return <ImageCapture key={key} accessKey={accessKey} />;
};

export default GenericCameraComponent;
